import { useCallback, useRef } from 'react';
import { useMask, UseMaskOptions } from '@mantine/hooks';

export interface ISyncedMask {
    /** Ref поля: новое поле получает состояние маски по своему тексту, без истории прежнего */
    ref: (node: HTMLInputElement | null) => void;
    /**
     * Текст поля, пришедший мимо ввода (значение владельца, откат, выбор в календаре): без `onChangeRaw` и с новой
     * историей Ctrl+Z. `keepHistory` — текст после ввода самого пользователя (разбор вставки): его можно отменить.
     */
    write: (text: string, keepHistory?: boolean) => void;
    /** Текст поля по маске */
    value: string;
}

/**
 * `useMask` (@mantine/hooks), состояние которого не расходится с полем. Разобранный текст и история Ctrl+Z живут в
 * хуке, а не в поле, и сверяются с полем только на его событии `input` и при подключении — и то, если поле не пустое.
 * Новое пустое поле (после disabled/readOnly) и текст, записанный мимо маски, оставляли ей прежний текст: фокус
 * возвращал его в поле, ввод правил его, Ctrl+Z возвращал значение до внешнего.
 *
 * Функция ref у `useMask` меняется с каждым вводом: React пере-прикрепил бы её, `useMask` заново присвоил бы
 * input.value, и Chrome увёл бы каретку в конец. Обработчики `useMask` читают опции через свой optionsRef — первой
 * функции достаточно.
 */
export const useSyncedMask = (options: UseMaskOptions): ISyncedMask => {
    const silentRef = useRef(false);
    const { onChangeRaw } = options;
    const {
        ref: maskRef,
        reset,
        value,
    } = useMask({
        ...options,
        onChangeRaw: (raw, masked) => {
            if (!silentRef.current) onChangeRaw?.(raw, masked);
        },
    });
    const attach = useRef(maskRef).current;
    const nodeRef = useRef<HTMLInputElement | null>(null);

    const write = useCallback(
        (text: string, keepHistory = false) => {
            const node = nodeRef.current;
            silentRef.current = true;
            try {
                if (!text || !keepHistory) reset();
                if (node && text) {
                    node.value = text;
                    attach(node);
                }
            } finally {
                silentRef.current = false;
            }
        },
        [attach, reset],
    );

    const ref = useCallback(
        (node: HTMLInputElement | null) => {
            if (node === nodeRef.current) return;
            nodeRef.current = node;
            attach(node);
            if (node) write(node.value);
        },
        [attach, write],
    );

    return { ref, write, value };
};
